import React, { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaBan,
  FaCheckCircle,
  FaChevronDown,
  FaChevronUp,
  FaClipboardCheck,
  FaClipboardList,
  FaDollarSign,
  FaEdit,
  FaInbox,
  FaUndo,
  FaUserCheck,
} from "react-icons/fa";
import {
  compactWorkflowStatusLabel,
  getCommissionStatusTone,
  isApplicationCommissionClosed,
  isApplicationCommissionCompleted,
  isApplicationFinalReviewPending,
  resolveApplicationAgentDisplay,
  resolveApplicationBranchDisplay,
} from "./commissionsConstants";
import { isRowEligibleForBulkAction } from "./commissionRowActionEligibility";
import { isCommissionRowSelectableForBulk } from "./commissionRowSelection";
import CommissionsStatusHistory from "./CommissionsStatusHistory";
import CommissionsTableSkeleton from "./CommissionsTableSkeleton";
import ClientDocumentsModal from "./ClientDocumentsModal";
import ClientDocumentsButton from "./ClientDocumentsButton";
import { resolveCommissionAppType } from "./clientDocumentsUtils";
import {
  canOpenCommissionDetail,
  commissionDetailPath,
  getCommissionDetailDisabledReason,
} from "./commissionDetailUtils";
import {
  formatInquiryDisplayDate,
  getInquiryFieldsFromApplication,
  getMissingInquiryBatchFields,
  isBatchItemFieldMissing,
} from "./inquiryApplicationUtils";
import {
  getCommissionCourseLabel,
  getCommissionRowKey,
} from "./commissionRowUtils";
import CommissionsRowActionsToolbar from "./CommissionsRowActionsToolbar";

const TABLE_CELL_CLASS = "px-3 py-2.5";
const TABLE_HEAD_CLASS =
  "border-b border-gray-200 bg-gray-50 text-[11px] font-semibold uppercase tracking-wide text-gray-500";

function getInquiryBatchInfo(application = {}) {
  const batch = application?.inquiry_batch;

  if (!batch?.batch_id) return null;

  return {
    id: batch.batch_id,
    label: String(batch.batch_no || `Batch #${batch.batch_id}`).trim(),
  };
}

const STATUS_TONE_CLASS = {
  emerald: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  sky: "bg-sky-50 text-sky-700 ring-sky-200",
  amber: "bg-amber-50 text-amber-800 ring-amber-200",
  orange: "bg-orange-50 text-orange-800 ring-orange-200",
  rose: "bg-rose-50 text-rose-700 ring-rose-200",
  gray: "bg-gray-100 text-gray-600 ring-gray-200",
};

/**
 * Derived workflow value (outcome / commission status). A blank value means the
 * automation has not evaluated this student yet — shown as a dash, not an error.
 */
function WorkflowStatusCell({ value, title, compact = true }) {
  const text = String(value || "").trim();

  if (!text) {
    return <span className="text-gray-400">—</span>;
  }

  const tone = STATUS_TONE_CLASS[getCommissionStatusTone(text)];
  const tooltip = title || text;
  const display = compact
    ? compactWorkflowStatusLabel(text).display
    : text;

  return (
    <span
      title={tooltip}
      className={`cms-commissions-workflow-badge inline-flex max-w-[7.25rem] truncate whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ${tone}`}
    >
      {display}
    </span>
  );
}

function InquiryCell({
  value,
  missing,
  mono = false,
  truncate = false,
  compact = false,
}) {
  const raw = String(value || "").trim();
  const showMissing = missing || !raw;
  const full = raw;
  const display =
    showMissing || !full
      ? ""
      : compact
        ? compactWorkflowStatusLabel(full).display
        : full;
  const tooltip = !showMissing && full ? full : undefined;
  const compactWidth = compact || truncate;

  return (
    <span
      className={`cms-commissions-inquiry-cell block text-[12px] font-medium ${
        compactWidth ? "max-w-[6.75rem] truncate" : ""
      } ${mono ? "tabular-nums font-mono text-[11px]" : ""} ${
        showMissing ? "text-red-500" : "text-slate-800"
      }`}
      title={tooltip}
    >
      {showMissing ? "—" : display}
    </span>
  );
}

function InquiryStudentCell({ name, clientId, missingFields = [] }) {
  const displayName = String(name || "").trim();
  const missingCount = missingFields.length;
  const missingTitle =
    missingCount > 0
      ? `${missingCount} required field${missingCount === 1 ? "" : "s"} missing for inquiry`
      : undefined;

  const nameNode = clientId ? (
    <Link
      to={`/clients/view-client-detail/${clientId}`}
      className="cms-commissions-student-cell__name truncate text-[13px] font-semibold text-[#007BFF] hover:underline"
      title={displayName || undefined}
    >
      {displayName || "—"}
    </Link>
  ) : (
    <span
      className="cms-commissions-student-cell__name truncate text-[13px] font-semibold text-slate-900"
      title={displayName || undefined}
    >
      {displayName || "—"}
    </span>
  );

  return (
    <div className="cms-commissions-student-cell min-w-0">
      <div className="cms-commissions-student-cell__name-wrap">
        {nameNode}
        {missingCount > 0 ? (
          <span
            className="cms-commissions-student-cell__flag"
            title={missingTitle}
            aria-label={missingTitle}
          >
            {missingCount}
          </span>
        ) : null}
      </div>
    </div>
  );
}

function CommissionRowClosedLabel() {
  return (
    <span
      className="cms-commissions-row-closed-label inline-block cursor-not-allowed select-none text-[13px] font-semibold uppercase tracking-wide text-gray-400"
      title="Commission case closed — no further actions"
      aria-disabled="true"
    >
      Closed
    </span>
  );
}

function CommissionSelectCheckbox({
  application,
  activeReport,
  rowKey,
  selectedSet,
  onToggleSelect,
  onBlockedCommissionSelect,
  inquiryBatchInfo,
}) {
  const selectable = isCommissionRowSelectableForBulk(application, activeReport);
  const checked = selectable && selectedSet.has(rowKey);

  if (!selectable) {
    return (
      <span
        className="inline-flex cursor-not-allowed opacity-35"
        title="Closed — not available for bulk selection"
        role="presentation"
        onClick={(event) => {
          event.stopPropagation();
          onBlockedCommissionSelect?.();
        }}
      >
        <input
          type="checkbox"
          checked={false}
          disabled
          readOnly
          tabIndex={-1}
          aria-label="Closed commission case (cannot select)"
        />
      </span>
    );
  }

  const batchTitle = inquiryBatchInfo?.label
    ? `Already in batch ${inquiryBatchInfo.label} — will be skipped on create unless removed from that batch`
    : undefined;

  return (
    <input
      type="checkbox"
      checked={checked}
      onChange={() => onToggleSelect?.(application)}
      title={batchTitle}
      aria-label={`Select ${application?.clientname || "application"}`}
    />
  );
}

function buildRemoveFromCommissionableItem({
  application,
  isCommissionTab,
  isClosed,
  removeBusy,
  onRemoveFromCommissionable,
}) {
  const queueId = application?.queue_id;
  if (!isCommissionTab || isClosed || !queueId || !onRemoveFromCommissionable) {
    return null;
  }

  return {
    key: "remove-from-commissionable",
    group: "queue",
    show: true,
    type: "button",
    label: "Move to General Report",
    title: "Remove from Commissionable Students and list in General Report",
    tone: "danger",
    icon: <FaArrowLeft size={13} />,
    disabled: removeBusy,
    onClick: () => onRemoveFromCommissionable(application),
  };
}

function RowActions({
  application,
  activeReport,
  commissionView = "active",
  actionLoadingKey,
  onAddToCommission,
  onRemoveFromCommissionable,
  onEditInquiry,
  onDiscontinue,
  onReactivate,
  onMarkCompleted,
  onRevertCompleted,
  onSubmitFinalReview,
  onApproveFinalReview,
  onReturnFromFinalReview,
}) {
  const rowKey = getCommissionRowKey(application);
  const isGeneral = activeReport === "general";
  const isCommissionTab = activeReport === "commission";
  const isCompletedView = commissionView === "completed";
  const isExceptionsReconciliationView =
    commissionView === "exceptions_reconciliation";
  const isFinalReviewView = commissionView === "final_review";
  const isBusy = Boolean(
    actionLoadingKey && rowKey && actionLoadingKey.endsWith(`-${rowKey}`),
  );
  const discontinueBusy = actionLoadingKey === `discontinue-${rowKey}`;
  const reactivateBusy = actionLoadingKey === `reactivate-${rowKey}`;
  const completedBusy = actionLoadingKey === `completed-${rowKey}`;
  const revertBusy = actionLoadingKey === `revert-${rowKey}`;
  const submitReviewBusy = actionLoadingKey === `submit-review-${rowKey}`;
  const approveReviewBusy = actionLoadingKey === `approve-review-${rowKey}`;
  const returnReviewBusy = actionLoadingKey === `return-review-${rowKey}`;
  const removeBusy = actionLoadingKey === `remove-commissionable-${rowKey}`;
  const detailEnabled = canOpenCommissionDetail(application);
  const detailDisabledReason = getCommissionDetailDisabledReason(application);
  const alreadyCommissionable = Boolean(application?.is_commissionable);
  const alreadyCompleted = isApplicationCommissionCompleted(application);
  const isClosed = isApplicationCommissionClosed(application);
  const removeFromCommissionableItem = buildRemoveFromCommissionableItem({
    application,
    isCommissionTab,
    isClosed,
    removeBusy,
    onRemoveFromCommissionable,
  });
  const isPendingFinalReview = isApplicationFinalReviewPending(application);
  const finalReviewOrigin = String(application?.final_review_origin || "").trim();
  const restoreFromCompletedOrigin = finalReviewOrigin === "completed";
  const rowActionContext = { activeReport, commissionView };
  const viewCommissionDetailItem = {
    key: "view-detail",
    group: "records",
    show: true,
    type: "link",
    label: "View commission details",
    title: detailEnabled
      ? "View commission details"
      : detailDisabledReason || "View commission details",
    icon: <FaClipboardList size={14} />,
    to: detailEnabled ? commissionDetailPath(application) : null,
    disabled: !detailEnabled,
  };

  if (isCommissionTab && isClosed) {
    return <CommissionRowClosedLabel />;
  }

  if (isCommissionTab && isFinalReviewView && isPendingFinalReview) {
    const finalReviewItems = [
      {
        key: "approve-final-review",
        show: true,
        type: "button",
        label: "Approve and close",
        title: "Approve and close this commission case",
        tone: "success",
        icon: <FaCheckCircle size={13} />,
        disabled: approveReviewBusy,
        onClick: () => onApproveFinalReview?.(application),
      },
      {
        key: "return-from-final-review",
        show: true,
        type: "button",
        label: restoreFromCompletedOrigin
          ? "Restore to Completed"
          : "Restore to Exceptions",
        title: restoreFromCompletedOrigin
          ? "Return to the Completed tab"
          : "Return to the Exceptions tab",
        icon: <FaUndo size={13} />,
        disabled: returnReviewBusy,
        onClick: () => onReturnFromFinalReview?.(application),
      },
      ...(removeFromCommissionableItem ? [removeFromCommissionableItem] : []),
      viewCommissionDetailItem,
    ];

    return (
      <CommissionsRowActionsToolbar
        items={finalReviewItems}
        disabled={isBusy}
        ariaLabel="Final review actions"
      />
    );
  }

  if (
    isCommissionTab &&
    isCompletedView &&
    !isPendingFinalReview &&
    !isClosed
  ) {
    const completedItems = [
      {
        key: "send-final-review",
        show: isRowEligibleForBulkAction(
          application,
          "submit_final_review",
          rowActionContext,
        ),
        type: "button",
        label: "Send for final review",
        title: "Move to Final Review",
        icon: <FaUserCheck size={13} />,
        disabled: submitReviewBusy,
        onClick: () => onSubmitFinalReview?.(application),
      },
      {
        key: "revert-completed",
        show: isRowEligibleForBulkAction(
          application,
          "revert_completed",
          rowActionContext,
        ),
        type: "button",
        label: "Restore to previous tab",
        title:
          "Restore the previous workflow status (usually Active or Exceptions)",
        icon: <FaUndo size={13} />,
        disabled: revertBusy,
        onClick: () => onRevertCompleted?.(application),
      },
      ...(removeFromCommissionableItem ? [removeFromCommissionableItem] : []),
      viewCommissionDetailItem,
    ];

    return (
      <CommissionsRowActionsToolbar
        items={completedItems}
        disabled={isBusy}
        ariaLabel="Completed tab actions"
      />
    );
  }

  if (
    isCommissionTab &&
    isExceptionsReconciliationView &&
    !isPendingFinalReview &&
    !isClosed
  ) {
    const exceptionItems = [
      {
        key: "reactivate",
        show: isRowEligibleForBulkAction(application, "reactivate", rowActionContext),
        type: "button",
        label: "Restore to active",
        title: "Restore to Active",
        icon: <FaUndo size={13} />,
        disabled: reactivateBusy,
        onClick: () => onReactivate?.(application),
      },
      {
        key: "send-final-review",
        show: isRowEligibleForBulkAction(
          application,
          "submit_final_review",
          rowActionContext,
        ),
        type: "button",
        label: "Send for final review",
        title: "Move to Final Review",
        icon: <FaUserCheck size={13} />,
        disabled: submitReviewBusy,
        onClick: () => onSubmitFinalReview?.(application),
      },
      ...(removeFromCommissionableItem ? [removeFromCommissionableItem] : []),
    ];

    return (
      <CommissionsRowActionsToolbar
        items={exceptionItems}
        disabled={isBusy}
        ariaLabel="Exceptions tab actions"
      />
    );
  }

  const showDiscontinue = isRowEligibleForBulkAction(
    application,
    "discontinue",
    rowActionContext,
  );
  const showReactivate = isRowEligibleForBulkAction(
    application,
    "reactivate",
    rowActionContext,
  );
  const showMarkCompleted = isRowEligibleForBulkAction(
    application,
    "mark_completed",
    rowActionContext,
  );
  const showRevertCompleted = isRowEligibleForBulkAction(
    application,
    "revert_completed",
    rowActionContext,
  );
  const showSendForFinalReview = isRowEligibleForBulkAction(
    application,
    "submit_final_review",
    rowActionContext,
  );
  const showApproveFinalReview = isRowEligibleForBulkAction(
    application,
    "approve_final_review",
    rowActionContext,
  );
  const showReturnFromFinalReview = isRowEligibleForBulkAction(
    application,
    "return_final_review",
    rowActionContext,
  );

  const menuItems = [
    {
      key: "discontinue",
      group: "workflow",
      show: showDiscontinue,
      type: "button",
      label: "Mark as discontinued",
      title: "Mark as Discontinued",
      tone: "danger",
      icon: <FaBan size={13} />,
      disabled: discontinueBusy,
      onClick: () => onDiscontinue?.(application),
    },
    {
      key: "reactivate",
      group: "workflow",
      show: showReactivate,
      type: "button",
      label: "Restore to active",
      title: "Restore to Active",
      icon: <FaUndo size={13} />,
      disabled: reactivateBusy,
      onClick: () => onReactivate?.(application),
    },
    {
      key: "mark-completed",
      group: "workflow",
      show: showMarkCompleted,
      type: "button",
      label: "Mark as completed",
      title: "Move to Completed — all commission reconciled",
      tone: "success",
      icon: <FaClipboardCheck size={14} />,
      disabled: completedBusy,
      onClick: () => onMarkCompleted?.(application),
    },
    {
      key: "revert-completed",
      group: "workflow",
      show: showRevertCompleted,
      type: "button",
      label: "Move back to active",
      title: "Move back to Active",
      icon: <FaUndo size={13} />,
      disabled: revertBusy,
      onClick: () => onRevertCompleted?.(application),
    },
    {
      key: "send-final-review",
      group: "workflow",
      show: showSendForFinalReview,
      type: "button",
      label: "Send for final review",
      title: "Send for final review",
      icon: <FaUserCheck size={13} />,
      disabled: submitReviewBusy,
      onClick: () => onSubmitFinalReview?.(application),
    },
    {
      key: "approve-final-review",
      group: "workflow",
      show: showApproveFinalReview,
      type: "button",
      label: "Approve and close",
      title: "Approve and close",
      tone: "success",
      icon: <FaCheckCircle size={13} />,
      disabled: approveReviewBusy,
      onClick: () => onApproveFinalReview?.(application),
    },
    {
      key: "return-from-final-review",
      group: "workflow",
      show: showReturnFromFinalReview,
      type: "button",
      label: "Return to reconciliation",
      title: "Return to Exceptions & Reconciliation",
      icon: <FaUndo size={13} />,
      disabled: returnReviewBusy,
      onClick: () => onReturnFromFinalReview?.(application),
    },
    {
      key: "add-commission",
      group: "queue",
      show: isGeneral && !alreadyCommissionable,
      type: "button",
      label: "Move to commissionable",
      title: "Move to commissionable",
      icon: <FaDollarSign size={14} />,
      onClick: () => onAddToCommission?.(application),
    },
    {
      key: "already-commissionable",
      group: "queue",
      show: isGeneral && alreadyCommissionable,
      type: "button",
      label: "Already commissionable",
      title: "Already in Commissionable Students",
      tone: "muted",
      icon: <FaCheckCircle size={13} />,
      disabled: true,
      onClick: () => {},
    },
    ...(removeFromCommissionableItem ? [removeFromCommissionableItem] : []),
    {
      key: "edit-inquiry",
      group: "records",
      show:
        isCommissionTab &&
        !isFinalReviewView &&
        !isCompletedView &&
        !isExceptionsReconciliationView,
      type: "button",
      label: "Edit inquiry details",
      title: "Edit inquiry details",
      icon: <FaEdit size={13} />,
      onClick: () => onEditInquiry?.(application),
    },
    {
      ...viewCommissionDetailItem,
      show:
        isCommissionTab &&
        !isFinalReviewView &&
        !isCompletedView &&
        !isExceptionsReconciliationView,
    },
  ];

  return (
    <CommissionsRowActionsToolbar
      items={menuItems}
      disabled={isBusy}
      ariaLabel="Application actions"
    />
  );
}

function SortIcon({ active, direction }) {
  if (!active) {
    return (
      <span className="inline-flex flex-col text-gray-300">
        <FaChevronUp size={7} />
        <FaChevronDown size={7} className="-mt-0.5" />
      </span>
    );
  }
  return direction === "asc" ? (
    <FaChevronUp size={8} className="text-[#007BFF]" />
  ) : (
    <FaChevronDown size={8} className="text-[#007BFF]" />
  );
}

function getCommissionInstallmentSummary(application) {
  const commission = application?.commission;
  if (!commission || typeof commission !== "object") return null;

  const total = Number(commission.total_installments);
  const hasTotal = Number.isFinite(total) && total >= 0;
  const paid = Number(commission.paid_installments);

  if (!hasTotal && !Number.isFinite(paid)) return null;

  return {
    total: hasTotal ? total : null,
    paid: Number.isFinite(paid) ? Math.max(0, paid) : null,
  };
}

function InstallmentCountCell({ value }) {
  if (value == null) {
    return <span className="text-gray-400">—</span>;
  }

  return (
    <span className="tabular-nums font-semibold text-gray-900">{value}</span>
  );
}

function getHeaderClassName(columnKey) {
  if (columnKey === "actions") return "text-right";
  if (columnKey === "select" || columnKey === "rowNumber") {
    return "w-10 text-center";
  }
  return "text-left";
}

function renderCommissionCell(columnKey, ctx) {
  const {
    application,
    fields,
    missingFields,
    rowKey,
    rowNumber,
    selectedSet,
    batchInfo,
    intakeValue,
    onToggleSelect,
    onBlockedCommissionSelect,
    navigate,
    setDocumentsModal,
    activeReport,
    actionLoadingKey,
    onAddToCommission,
    onRemoveFromCommissionable,
    onEditInquiry,
    onDiscontinue,
    onReactivate,
    onMarkCompleted,
    commissionView,
    onRevertCompleted,
    onSubmitFinalReview,
    onApproveFinalReview,
    onReturnFromFinalReview,
  } = ctx;

  switch (columnKey) {
    case "select":
      return (
        <CommissionSelectCheckbox
          application={application}
          activeReport={activeReport}
          rowKey={rowKey}
          selectedSet={selectedSet}
          onToggleSelect={onToggleSelect}
          onBlockedCommissionSelect={onBlockedCommissionSelect}
          inquiryBatchInfo={batchInfo}
        />
      );
    case "rowNumber":
      return rowNumber;
    case "clientName":
      return (
        <InquiryStudentCell
          name={fields.clientName || application?.clientname}
          clientId={fields.clientId || application?.clientid}
          missingFields={missingFields}
        />
      );
    case "studentId":
      return (
        <InquiryCell
          value={fields.studentId}
          missing={isBatchItemFieldMissing("studentId", fields)}
          mono
        />
      );
    case "dob":
      return (
        <InquiryCell
          value={formatInquiryDisplayDate(fields.dob)}
          missing={isBatchItemFieldMissing("dob", fields)}
          mono
        />
      );
    case "course":
      return (
        <InquiryCell
          value={fields.course || application?.coursename}
          missing={isBatchItemFieldMissing("course", fields)}
          truncate
          compact
        />
      );
    case "provider":
      return (
        <InquiryCell
          value={fields.provider || application?.providername}
          missing={isBatchItemFieldMissing("provider", fields)}
          truncate
          compact
        />
      );
    case "processing_agent":
      return (
        <InquiryCell
          value={resolveApplicationAgentDisplay(application, "processing")}
          missing={false}
          truncate
        />
      );
    case "referring_agent":
      return (
        <InquiryCell
          value={resolveApplicationAgentDisplay(application, "referring")}
          missing={false}
          truncate
        />
      );
    case "processing_branch":
      return (
        <InquiryCell
          value={resolveApplicationBranchDisplay(application, "processing")}
          missing={false}
          truncate
        />
      );
    case "referring_branch":
      return (
        <InquiryCell
          value={resolveApplicationBranchDisplay(application, "referring")}
          missing={false}
          truncate
        />
      );
    case "intake":
      return (
        <InquiryCell
          value={intakeValue}
          missing={!String(intakeValue || "").trim()}
          truncate
          compact
        />
      );
    case "history_status_title":
      return (
        <CommissionsStatusHistory
          statusHistory={application?.status_history}
          fallbackTitle={
            application?.history_status_title ||
            application?.status_title ||
            "—"
          }
        />
      );
    case "start":
      return (
        <InquiryCell
          value={formatInquiryDisplayDate(fields.start)}
          missing={isBatchItemFieldMissing("start", fields)}
        />
      );
    case "end":
      return (
        <InquiryCell
          value={formatInquiryDisplayDate(fields.end)}
          missing={isBatchItemFieldMissing("end", fields)}
        />
      );
    case "student_outcome":
      return <WorkflowStatusCell value={application?.student_outcome} />;
    case "commission_workflow_status":
      return (
        <WorkflowStatusCell
          value={application?.commission_workflow_status}
          title={
            application?.commission_eligibility
              ? `Eligibility: ${application.commission_eligibility}`
              : undefined
          }
        />
      );
    case "documents":
      return (
        <ClientDocumentsButton
          documents={application?.documents}
          clientName={fields.clientName || application?.clientname || "Client"}
          clientId={application?.clientid}
          providerId={application?.providerid}
          appId={application?.application_id}
          appType={resolveCommissionAppType(application?.category)}
          onOpen={setDocumentsModal}
        />
      );
    case "batch":
      return batchInfo?.id ? (
        <button
          type="button"
          onClick={() => navigate(`/commissions/inquiry-batches/${batchInfo.id}`)}
          className="font-semibold text-blue-600 hover:underline"
        >
          {batchInfo.label}
        </button>
      ) : (
        <span className="text-gray-400">—</span>
      );
    case "actions":
      return (
        <RowActions
          application={application}
          activeReport={activeReport}
          commissionView={commissionView}
          actionLoadingKey={actionLoadingKey}
          onAddToCommission={onAddToCommission}
          onRemoveFromCommissionable={onRemoveFromCommissionable}
          onEditInquiry={onEditInquiry}
          onDiscontinue={onDiscontinue}
          onReactivate={onReactivate}
          onMarkCompleted={onMarkCompleted}
          onRevertCompleted={onRevertCompleted}
          onSubmitFinalReview={onSubmitFinalReview}
          onApproveFinalReview={onApproveFinalReview}
          onReturnFromFinalReview={onReturnFromFinalReview}
        />
      );
    default:
      return "—";
  }
}

function renderGeneralCell(columnKey, ctx) {
  const {
    application,
    rowNumber,
    selectedSet,
    courseName,
    appLink,
    fallbackTitle,
    installmentSummary,
    onToggleSelect,
    setDocumentsModal,
    activeReport,
    actionLoadingKey,
    onAddToCommission,
    onRemoveFromCommissionable,
    onDiscontinue,
    onReactivate,
    onMarkCompleted,
    commissionView,
    onRevertCompleted,
    onSubmitFinalReview,
    onApproveFinalReview,
    onReturnFromFinalReview,
  } = ctx;

  switch (columnKey) {
    case "select":
      return (
        <input
          type="checkbox"
          checked={selectedSet.has(getCommissionRowKey(application))}
          onChange={() => onToggleSelect?.(application)}
          aria-label={`Select ${application?.clientname || "application"}`}
        />
      );
    case "rowNumber":
      return rowNumber;
    case "clientname":
      return (
        <Link
          to={`/clients/view-client-detail/${application?.clientid}`}
          className="line-clamp-2 font-medium text-[#007BFF] transition hover:text-[#0069D9] hover:underline"
        >
          {application?.clientname || "-"}
        </Link>
      );
    case "external_processing_agent":
      return (
        <span className="line-clamp-2 text-[12px] text-gray-600">
          {resolveApplicationAgentDisplay(application, "processing") || "—"}
        </span>
      );
    case "external_referring_agent":
      return (
        <span className="line-clamp-2 text-[12px] text-gray-600">
          {resolveApplicationAgentDisplay(application, "referring") || "—"}
        </span>
      );
    case "processing_branch":
      return (
        <span className="line-clamp-2 text-[12px] text-gray-600">
          {resolveApplicationBranchDisplay(application, "processing") || "—"}
        </span>
      );
    case "referring_branch":
      return (
        <span className="line-clamp-2 text-[12px] text-gray-600">
          {resolveApplicationBranchDisplay(application, "referring") || "—"}
        </span>
      );
    case "providername":
      return (
        <div className="min-w-0 max-w-[220px]">
          <p className="line-clamp-1 font-medium text-gray-900">
            {application?.providername || "-"}
          </p>
          <Link
            to={appLink}
            className="line-clamp-1 text-[12px] text-[#007BFF] transition hover:text-[#0069D9] hover:underline"
          >
            {courseName}
          </Link>
        </div>
      );
    case "history_status_title":
      return (
        <CommissionsStatusHistory
          statusHistory={application?.status_history}
          fallbackTitle={fallbackTitle}
        />
      );
    case "intake":
      return application?.intake || "—";
    case "documents":
      return (
        <ClientDocumentsButton
          documents={application?.documents}
          clientName={application?.clientname || "Client"}
          clientId={application?.clientid}
          providerId={application?.providerid}
          appId={application?.application_id}
          appType={resolveCommissionAppType(application?.category)}
          onOpen={setDocumentsModal}
        />
      );
    case "total_installments":
      return <InstallmentCountCell value={installmentSummary?.total} />;
    case "paid_installments":
      return <InstallmentCountCell value={installmentSummary?.paid} />;
    case "statuschangedate":
      return application?.statuschangedate || "-";
    case "actions":
      return (
        <RowActions
          application={application}
          activeReport={activeReport}
          commissionView={commissionView}
          actionLoadingKey={actionLoadingKey}
          onAddToCommission={onAddToCommission}
          onRemoveFromCommissionable={onRemoveFromCommissionable}
          onDiscontinue={onDiscontinue}
          onReactivate={onReactivate}
          onMarkCompleted={onMarkCompleted}
          onRevertCompleted={onRevertCompleted}
          onSubmitFinalReview={onSubmitFinalReview}
          onApproveFinalReview={onApproveFinalReview}
          onReturnFromFinalReview={onReturnFromFinalReview}
        />
      );
    default:
      return "—";
  }
}

function CommissionsTable({
  applications,
  loading,
  sortBy,
  sortDir,
  onSort,
  embedded = false,
  activeReport = "general",
  commissionView = "active",
  actionLoadingKey = null,
  onAddToCommission,
  onRemoveFromCommissionable,
  onDiscontinue,
  onReactivate,
  onMarkCompleted,
  onRevertCompleted,
  onSubmitFinalReview,
  onApproveFinalReview,
  onReturnFromFinalReview,
  selectedKeys,
  onToggleSelect,
  onToggleSelectAll,
  onBlockedCommissionSelect,
  onEditInquiry,
  visibleColumns = [],
  providerFilterActive = false,
}) {
  const navigate = useNavigate();
  const [documentsModal, setDocumentsModal] = useState(null);

  const handleDocumentsModalChange = useCallback((docs) => {
    if (!Array.isArray(docs)) {
      return Promise.resolve(docs);
    }
    setDocumentsModal((prev) => (prev ? { ...prev, documents: docs } : null));
    return Promise.resolve(docs);
  }, []);

  const columns = visibleColumns;
  const isCommissionTab = activeReport === "commission";

  if (loading) {
    return (
      <CommissionsTableSkeleton
        rows={10}
        embedded={embedded}
        columns={columns}
      />
    );
  }

  const selectedSet =
    selectedKeys instanceof Set ? selectedKeys : new Set(selectedKeys || []);

  const selectableApps = (applications || []).filter((app) =>
    isCommissionRowSelectableForBulk(app, activeReport),
  );

  const allSelectableChecked =
    selectableApps.length > 0 &&
    selectableApps.every((app) => selectedSet.has(getCommissionRowKey(app)));

  const table = (
    <div className="cms-commissions-table-wrap overflow-x-auto">
      <table
        className={`w-full text-left text-[13px] ${
          isCommissionTab ? "min-w-[960px]" : "min-w-[760px]"
        }`}
      >
        <thead className={TABLE_HEAD_CLASS}>
          <tr>
            {columns.map((column) => {
              const sortable =
                column.sortable !== false &&
                column.key !== "actions" &&
                column.key !== "select" &&
                column.key !== "rowNumber";
              const isActive = sortable && sortBy === column.key;
              return (
                <th
                  key={column.key}
                  className={`whitespace-nowrap ${TABLE_CELL_CLASS} ${getHeaderClassName(column.key)}`}
                >
                  {column.key === "select" ? (
                    <input
                      type="checkbox"
                      checked={allSelectableChecked}
                      onChange={() => onToggleSelectAll?.(selectableApps)}
                      disabled={selectableApps.length === 0}
                      title={
                        providerFilterActive
                          ? "Select students on this page only (use Select all for bulk above for every student for this provider)"
                          : undefined
                      }
                      aria-label={
                        providerFilterActive
                          ? "Select students on this page"
                          : "Select all applications on this page"
                      }
                    />
                  ) : sortable ? (
                    <button
                      type="button"
                      onClick={() => onSort(column.key)}
                      className="inline-flex items-center gap-1 transition hover:text-gray-800"
                      style={{
                        border: "none",
                        outline: "none",
                        background: "transparent",
                        padding: 0,
                      }}
                    >
                      {column.title}
                      <SortIcon active={isActive} direction={sortDir} />
                    </button>
                  ) : (
                    column.title
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 bg-white">
          {applications?.length > 0 ? (
            applications.map((application, index) => {
              const rowNumber = index + 1;

              if (isCommissionTab) {
                const fields = getInquiryFieldsFromApplication(application);
                const missingFields = getMissingInquiryBatchFields(fields);
                const rowKey = getCommissionRowKey(application);
                const batchInfo = getInquiryBatchInfo(application);
                const intakeValue = fields.intake || application?.intake;
                const ctx = {
                  application,
                  fields,
                  missingFields,
                  rowKey,
                  rowNumber,
                  selectedSet,
                  batchInfo,
                  intakeValue,
                  onToggleSelect,
                  onBlockedCommissionSelect,
                  navigate,
                  setDocumentsModal,
                  activeReport,
                  commissionView,
                  actionLoadingKey,
                  onAddToCommission,
                  onRemoveFromCommissionable,
                  onEditInquiry,
                  onDiscontinue,
                  onReactivate,
                  onMarkCompleted,
                  onRevertCompleted,
                  onSubmitFinalReview,
                  onApproveFinalReview,
                  onReturnFromFinalReview,
                };

                const rowSelected =
                  selectedSet.has(rowKey) &&
                  isCommissionRowSelectableForBulk(application, activeReport);

                return (
                  <tr
                    key={rowKey}
                    className={`transition hover:bg-[#F8FAFC] ${
                      rowSelected ? "bg-[#F0F7FF]" : ""
                    }`}
                  >
                    {columns.map((column) => (
                      <td
                        key={`${rowKey}-${column.key}`}
                        className={`${TABLE_CELL_CLASS} ${
                          column.key === "actions"
                            ? "text-right"
                            : column.key === "select" || column.key === "rowNumber"
                              ? "w-10 text-center tabular-nums text-gray-500"
                              : ""
                        }`}
                      >
                        {renderCommissionCell(column.key, ctx)}
                      </td>
                    ))}
                  </tr>
                );
              }

              const courseName = getCommissionCourseLabel(application);
              const appLink =
                application?.category === "education"
                  ? `/applications/view-education-application-detail/${application?.application_id}`
                  : `/applications/view-migration-application-detail/${application?.application_id}`;
              const fallbackTitle =
                application?.history_status_title ||
                application?.status_title ||
                "—";
              const installmentSummary =
                getCommissionInstallmentSummary(application);
              const rowKey = getCommissionRowKey(application);
              const ctx = {
                application,
                rowNumber,
                selectedSet,
                courseName,
                appLink,
                fallbackTitle,
                installmentSummary,
                onToggleSelect,
                setDocumentsModal,
                activeReport,
                commissionView,
                actionLoadingKey,
                onAddToCommission,
                onRemoveFromCommissionable,
                onDiscontinue,
                onReactivate,
                onMarkCompleted,
                onRevertCompleted,
                onSubmitFinalReview,
                onApproveFinalReview,
                onReturnFromFinalReview,
              };

              return (
                <tr
                  key={rowKey}
                  className={`transition hover:bg-[#F8FAFC] ${
                    selectedSet.has(getCommissionRowKey(application))
                      ? "bg-[#F0F7FF]"
                      : ""
                  }`}
                >
                  {columns.map((column) => (
                    <td
                      key={`${rowKey}-${column.key}`}
                      className={`${TABLE_CELL_CLASS} ${
                        column.key === "actions"
                          ? "text-right"
                          : column.key === "select" || column.key === "rowNumber"
                            ? "w-10 text-center tabular-nums text-gray-500"
                            : column.key === "intake" ||
                                column.key === "statuschangedate" ||
                                column.key === "total_installments" ||
                                column.key === "paid_installments"
                              ? "whitespace-nowrap text-gray-600"
                              : column.key === "external_processing_agent" ||
                                  column.key === "external_referring_agent" ||
                                  column.key === "processing_branch" ||
                                  column.key === "referring_branch"
                                ? "text-gray-600"
                                : ""
                      }`}
                    >
                      {renderGeneralCell(column.key, ctx)}
                    </td>
                  ))}
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan={Math.max(columns.length, 1)} className="px-3 py-10 text-center">
                <div className="mx-auto flex max-w-sm flex-col items-center">
                  <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100">
                    <FaInbox size={16} className="text-gray-400" />
                  </div>
                  <p className="text-[15px] font-semibold text-gray-900">
                    No applications found
                  </p>
                  <p className="mt-0.5 text-[13px] text-gray-500">
                    Try adjusting your filters to see more results
                  </p>
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

  if (embedded) {
    return (
      <>
        {table}
        <ClientDocumentsModal
          open={Boolean(documentsModal)}
          clientName={documentsModal?.clientName}
          documents={documentsModal?.documents}
          clientId={documentsModal?.clientId}
          providerId={documentsModal?.providerId}
          appId={documentsModal?.appId}
          appType={documentsModal?.appType}
          onDocumentsChange={handleDocumentsModalChange}
          onClose={() => setDocumentsModal(null)}
        />
      </>
    );
  }

  return (
    <div className="overflow-hidden rounded-md border border-gray-200 bg-white shadow-sm">
      {table}
      <ClientDocumentsModal
        open={Boolean(documentsModal)}
        clientName={documentsModal?.clientName}
        documents={documentsModal?.documents}
        clientId={documentsModal?.clientId}
        providerId={documentsModal?.providerId}
        appId={documentsModal?.appId}
        appType={documentsModal?.appType}
        onDocumentsChange={handleDocumentsModalChange}
        onClose={() => setDocumentsModal(null)}
      />
    </div>
  );
}

export default CommissionsTable;
